"use client";

import { motion } from "framer-motion";
import { useState } from "react";
import { AnimatedBackground } from "@/components/ui/animated-background";

const USE_CASES = [
  {
    id: "analytics",
    tab: "Data Analytics",
    title: "Data Analytics",
    description: "Make data-driven decisions",
    video: "https://fellou.s3.us-west-1.amazonaws.com/videos/website-main-page/use+case/Data+Analytics.webm",
  },
  {
    id: "career",
    tab: "Career Growth",
    title: "Career Growth",
    description: "Advance your career with AI-powered tools",
    video: "https://fellou.s3.us-west-1.amazonaws.com/videos/website-main-page/use+case/Career+Development.webm",
  },
  {
    id: "marketing",
    tab: "Marketing",
    title: "Marketing",
    description: "Supercharge your marketing campaigns",
    video: "https://fellou.s3.us-west-1.amazonaws.com/videos/website-main-page/use+case/Marketing+.webm",
  },
  {
    id: "study",
    tab: "Study",
    title: "Study",
    description: "Enhance your learning experience",
    video: "https://fellou.s3.us-west-1.amazonaws.com/videos/website-main-page/use+case/Study.webm",
  },
  {
    id: "productivity",
    tab: "Productivity",
    title: "Productivity",
    description: "Boost your daily productivity",
    video: "https://fellou.s3.us-west-1.amazonaws.com/videos/website-main-page/use+case/Productivity.webm",
  },
  {
    id: "daily-life",
    tab: "Daily Life",
    title: "Daily Life",
    description: "Simplify your everyday tasks",
    video: "https://fellou.s3.us-west-1.amazonaws.com/videos/website-main-page/use+case/Daily+Life.webm",
  },
];

interface UserCaseSectionProps {
  onDownloadClick?: () => void;
}

export default function UserCaseSection({ onDownloadClick }: UserCaseSectionProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [activeUseCase, setActiveUseCase] = useState(USE_CASES[0].id);

  // Use case change handler
  const handleUseCaseChange = (newActiveId: string) => {
    setActiveUseCase(newActiveId);
    const newIndex = USE_CASES.findIndex((useCase) => useCase.id === newActiveId);
    if (newIndex !== -1) {
      setActiveIndex(newIndex);
    }
  };

  const currentUseCase = USE_CASES[activeIndex];

  return (
    <div className="h-[200vh] bg-transparent relative">
      <div className="sticky top-[60px] h-[calc(100vh-60px)] flex flex-col  ">
        {/* Top Section - Title (Fixed) */}
        <div className="flex-shrink-0 text-center py-4">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <h2 className="text-3xl text-white sm:text-4xl font-bold">What Fellou Can Do For You</h2>
            <p className="mt-2 text-sm text-white/60">
              <a href="/use-cases" className="hover:text-white/80 transition-colors">
                Discover more →
              </a>
            </p>
          </div>
        </div>

        {/* Middle Section - Video (Flexible based on remaining space) */}
        <div className="flex-1 flex items-center justify-center px-6 min-h-0">
          <video
            key={currentUseCase.id}
            autoPlay
            muted
            loop
            playsInline
            className="max-w-full max-h-full object-contain rounded-lg"
            style={{
              width: "auto",
              height: "auto",
            }}
          >
            <source src={currentUseCase.video} type="video/mp4" />
          </video>
        </div>

        {/* Bottom Section - Button Controls (Fixed) */}
        <div className="flex-shrink-0 py-4">
          <div className="mx-auto max-w-7xl overflow-x-auto px-6 text-center [scrollbar-width:none] lg:px-8">
            <div className="flex min-w-max items-center justify-center">
              <div className="z-0 flex flex-wrap justify-center space-x-2 rounded-full bg-white/20 px-2 py-2">
                <AnimatedBackground
                  className="whitespace-nowrap rounded-full bg-zinc-900"
                  transition={{
                    ease: "easeInOut",
                    duration: 0.2,
                  }}
                  defaultValue={activeUseCase}
                  onValueChange={(newActiveId) => {
                    handleUseCaseChange(newActiveId as string);
                  }}
                >
                  {USE_CASES.map((useCase, index) => (
                    <button
                      key={index}
                      data-id={useCase.id}
                      data-cursor-magnetic
                      type="button"
                      className="px-2 py-1 text-xs text-zinc-200 transition-colors duration-300 hover:text-zinc-400 data-[checked=true]:text-zinc-100 dark:text-zinc-200 sm:px-3 sm:py-1.5 sm:text-sm md:px-4 md:text-base"
                    >
                      {useCase.tab}
                    </button>
                  ))}
                </AnimatedBackground>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
